import { getCategoryFilters, getCategoryFiltersQueryKey } from '@/api/kubik/inputs-catalog'
import type { IAppRoute } from '@/components/composed/AppAccessControlProvider/types'
import { isDesktop } from '@/logic/app/reducer'
import { hasParamInUrl } from '@/logic/data/helpers'
import { getAsyncThunks } from '@/logic/data/thunks'
import { betsAuctionPrefetchQueries, betsVitrinaPrefetchSsrQueries } from '@/logic/index/bets/queries'
import { ssrPrefetchQueries } from '@/logic/index/ssrPrefetchQueries'
import { inputsAgriculturalCatalogSsrQueries } from '@/logic/inputs/ssrQueries'
import {
  journalByRubricSsrQueries,
  journalByTagSsrQueries,
  journalIndexSsrQueries,
  journalNewsSsrQueries,
  journalPublicationSsrQueries,
} from '@/logic/journal/ssrQueries'
import { ssrPrefetchQueriesForTenders } from '@/logic/tender/ssrPrefetchQueries'
import MobileRedirect from '@/pages/MobileRedirect/MobileRedirect'
import { fetchPoolingLanding } from '@/pages/Pooling/thunks'
import {
  EAgriculturalProductsRoute,
  EAgroservicesRoute,
  ECartRoute,
  ECommonRoute,
  EFinancingOfferRoute,
  EHarvestSaleRoute,
  EInputsPromoLandingRoute,
  EJournalRoute,
  EPartnerLandingRoute,
  EPoolingRoute,
  EUserProfileRoute,
} from '@/types'
import { ELegalDocumentsRoute } from '@/types/ELegalDocumentsRoute'
import { EOfferRequestsRoute } from '@/types/EOfferRequestsRoute'
import { EInfoportalAdministrationRoute } from '@/types/Infoportal'
import { fetchTemplates } from './logic/harvestSale/thunks'
import { fetchAgriculturalProduct } from './pages/AgriculturalProduct/thunks'
import { vezdehodPromoLanding } from './pages/promo'
import { getFaqBreadcrumbs } from './pages/QuestionsAndAnswers/helpers'
import QuestionsAndAnswers from './pages/QuestionsAndAnswers/QuestionsAndAnswers'
import { EFaqRouteUrl } from './pages/QuestionsAndAnswers/types'
import { messages } from './messages'
import {
  AboutUs,
  AdvancePaymentOnFutureHarvest,
  AdvancePaymentOnFutureHarvestRedirect,
  AgriculturalMachinesAndEquipmentLeasing,
  AgriculturalProduct,
  AgriculturalProductCatalog,
  AgriculturalProducts,
  Agrocalc,
  Agroconsulting,
  Agroservices,
  AlfaLeasing,
  BusinessProcess,
  CenterInvestLoan,
  CulturesSaleLots,
  EfisZsn,
  Error404Page,
  Error500Page,
  ExperimentalFinancing,
  Faq,
  FaqSearch,
  FarmlandCropValuation,
  Feedback,
  FgisZerno,
  FieldEvaluationReport,
  Financing,
  FinancingAgentPage,
  FinancingRedirect,
  FleetFinanceLeasing,
  HarvestSale,
  HarvestSaleLots,
  Impersonation,
  IndexPage,
  InfoportalAdministrationPage,
  Interleasing,
  InvestmentCredit,
  InvestSber,
  Journal,
  JournalNews,
  JournalPublication,
  JournalRedirectToLatestNews,
  LegalDocumentsPage,
  Logout,
  LombardCreditAlfaBank,
  Loyalty,
  MakingOrderDetails,
  MFOPage,
  MultiCart,
  OborotSber,
  OfferRequest,
  OfferRequests,
  PartnerLanding,
  PartnerLandingRedirect,
  PoleDiff,
  PoleExpert,
  PolePlus,
  PoleScouting,
  Pooling,
  QrCode,
  Requisites,
  Revolving,
  SberAuthRedirect,
  SberLeasing,
  SberRassrochka,
  SeedingRateCalc,
  StoneXxiLeasing,
  TemporaryBasicAuthPage,
  TenderComparativeTable,
  TenderPublic,
  TenderPublicOfferForm,
  TendersPublicList,
  TransportAndSpecialEquipmentLeasing,
  UserProfile,
  WorkingConditions,
  YouDo,
} from './pages'

const fetchAgroserviceProducts = getAsyncThunks('agroserviceProducts')[0]

export const ROUTES: IAppRoute[] = [
  {
    key: 'index-page',
    path: ECommonRoute.main,
    exact: true,
    component: IndexPage,
    ssrPrefetchQueries,
    accessControlKey: 'routes.index',
    metaInfo: {
      title: messages.indexPage.title,
    },
  },
  {
    key: 'agricultural-products',
    path: EAgriculturalProductsRoute.main,
    exact: true,
    component: AgriculturalProducts,
    ssrPrefetchQueries: () => [{ queryFn: getCategoryFilters, queryKey: getCategoryFiltersQueryKey() }],
    accessControlKey: 'routes.agriculturalProducts',
    metaInfo: {
      title: messages.agriculturalProductsPage.title,
    },
  },
  {
    key: 'agricultural-product-catalog',
    path: EAgriculturalProductsRoute.category,
    exact: true,
    component: AgriculturalProductCatalog,
    ssrPrefetchQueries: inputsAgriculturalCatalogSsrQueries,
    accessControlKey: 'routes.agriculturalProductsCatalog',
  },
  {
    key: 'agricultural-product',
    path: EAgriculturalProductsRoute.product,
    exact: true,
    component: AgriculturalProduct,
    asyncThunk: [fetchAgriculturalProduct],
    accessControlKey: 'routes.agriculturalProduct',
  },
  {
    key: 'user-profile',
    path: EUserProfileRoute.main,
    component: UserProfile,
    shouldBeAuthenticated: true,
    metaInfo: {
      title: messages.userProfilePage.title,
      robots: 'noindex',
    },
    accessControlKey: 'routes.userProfile',
  },
  // DEPRECATED. Пожалуйста не используйте
  {
    key: 'harvestSaleLotsOld',
    path: `${EHarvestSaleRoute.main}/lots/:cultureId/`,
    exact: true,
    component: HarvestSaleLots,
    asyncThunk: [fetchTemplates, ...getAsyncThunks({ name: 'cultureIndicators', shouldFetch: hasParamInUrl('cultureId') }, 'cultures')],
    accessControlKey: 'routes.harvestSaleLotsOld',
  },
  {
    key: 'harvestSaleLots',
    path: `${EHarvestSaleRoute.main}/:cultureId/lots`,
    exact: true,
    component: HarvestSaleLots,
    asyncThunk: [fetchTemplates, ...getAsyncThunks({ name: 'cultureIndicators', shouldFetch: hasParamInUrl('cultureId') }, 'cultures')],
    accessControlKey: 'routes.harvestSaleLots',
  },
  {
    key: 'CulturesSaleLots',
    path: `${EHarvestSaleRoute.main}/:cultureId`,
    exact: true,
    component: CulturesSaleLots,
    asyncThunk: [fetchTemplates, ...getAsyncThunks({ name: 'cultureIndicators', shouldFetch: hasParamInUrl('cultureId') }, 'cultures')],
    accessControlKey: 'routes.culturesSaleLots',
  },
  {
    key: 'Pooling',
    path: EPoolingRoute.POOLING,
    exact: true,
    component: Pooling,
    asyncThunk: [fetchPoolingLanding],
    accessControlKey: 'routes.pooling',
  },
  {
    key: 'harvestSale',
    path: EHarvestSaleRoute.main,
    exact: true,
    component: HarvestSale,
    asyncThunk: getAsyncThunks('cultures', 'harvestSalePage'),
    metaInfo: {
      title: messages.harvestSalePage.title,
    },
    accessControlKey: 'routes.harvestSale',
  },
  {
    key: 'tendersPublicList',
    path: ECommonRoute.tendersPublicList,
    exact: true,
    component: TendersPublicList,
    ssrPrefetchQueries: ssrPrefetchQueriesForTenders,
    metaInfo: {
      title: messages.tenderPublicListPage.title,
    },
    accessControlKey: 'routes.tendersPublicList',
  },
  {
    key: 'tender-public',
    path: ECommonRoute.tenderPublic,
    exact: true,
    component: TenderPublic,
    metaInfo: {
      title: messages.agroservices.title,
    },
    accessControlKey: 'routes.tenderPublic',
  },
  {
    key: 'tender-public-comparative-table',
    path: ECommonRoute.tenderPublicComparativeTable,
    exact: true,
    component: TenderComparativeTable,
    metaInfo: {
      title: messages.agroservices.title,
    },
    accessControlKey: 'routes.tenderPublicComparativeTable',
  },
  {
    key: 'tender-public-offer-from',
    path: ECommonRoute.tenderPublicOfferForm,
    exact: true,
    component: TenderPublicOfferForm,
    metaInfo: {
      title: messages.agroservices.title,
    },
    accessControlKey: 'routes.tenderPublicOfferFrom',
  },
  {
    key: 'offerRequests',
    path: EOfferRequestsRoute.main,
    exact: true,
    component: OfferRequests,
    ssrPrefetchQueries: betsVitrinaPrefetchSsrQueries,
    metaInfo: {
      title: messages.offerRequestsPage.title,
    },
    accessControlKey: 'routes.offerRequests',
  },
  {
    key: 'offerRequest',
    path: EOfferRequestsRoute.offer,
    exact: true,
    component: OfferRequest,
    ssrPrefetchQueries: betsAuctionPrefetchQueries,
    accessControlKey: 'routes.offerRequest',
  },
  {
    key: 'qr-code',
    path: ECommonRoute.qrCode,
    exact: true,
    component: QrCode,
    metaInfo: {
      title: messages.qrCode.title,
      robots: 'noindex',
    },
    accessControlKey: 'routes.qrCode',
  },
  {
    key: 'error-500',
    path: ECommonRoute.error500,
    exact: true,
    component: Error500Page,
    metaInfo: {
      title: messages.page500.title,
      robots: 'noindex',
    },
    accessControlKey: 'routes.error500',
  },
  {
    key: 'error-404',
    path: ECommonRoute.error404,
    exact: true,
    component: Error404Page,
    metaInfo: {
      title: messages.page400.title,
      robots: 'noindex',
    },
    accessControlKey: 'routes.error404',
  },
  {
    key: 'making-order-details',
    path: `${ECartRoute.makingOrderDetails}/:cartId`,
    exact: true,
    component: MakingOrderDetails,
    shouldBeAuthenticated: true,
    metaInfo: {
      title: messages.orderDetailPage.title,
      robots: 'noindex',
    },
    accessControlKey: 'routes.makingOrderDetails',
  },
  {
    key: 'multi-cart',
    path: ECartRoute.multiCart,
    exact: true,
    component: MultiCart,
    shouldBeAuthenticated: true,
    metaInfo: {
      title: messages.multiCart.title,
      robots: 'noindex',
    },
    accessControlKey: 'routes.multiCart',
  },
  {
    key: 'impersonation',
    path: ECommonRoute.impersonation,
    exact: true,
    component: Impersonation,
    shouldBeAuthenticated: true,
    metaInfo: {
      title: messages.impersonationPage.title,
      robots: 'noindex',
    },
    accessControlKey: 'routes.impersonation',
  },
  {
    key: 'temporaryBasicAuthPage',
    path: ECommonRoute.temporaryBasicAuthPage,
    exact: true,
    component: TemporaryBasicAuthPage,
    metaInfo: {
      title: messages.temporaryBasicAuthPage.title,
      robots: 'noindex',
    },
    accessControlKey: 'routes.temporaryBasicAuthPage',
  },
  {
    key: 'financing',
    path: EFinancingOfferRoute.main,
    exact: true,
    component: Financing,
    asyncThunk: getAsyncThunks('expressCreditWithRedirection'),
    metaInfo: {
      title: messages.financing.title,
    },
    accessControlKey: 'routes.financing',
  },
  {
    key: 'finances',
    path: EFinancingOfferRoute.experimentalMain,
    exact: true,
    component: ExperimentalFinancing,
    asyncThunk: getAsyncThunks('expressCreditWithRedirection'),
    metaInfo: {
      title: messages.financing.title,
    },
    accessControlKey: 'routes.finances',
  },
  {
    key: 'agroservices-calc',
    path: EAgroservicesRoute.agrocalc,
    exact: true,
    component: Agrocalc,
    metaInfo: {
      title: messages.agroservices.agrocacl.title,
    },
    accessControlKey: 'routes.agroservicesCalc',
  },
  {
    key: 'agroservices-seeding-rate',
    path: EAgroservicesRoute.seedingRateCalc,
    exact: true,
    component: SeedingRateCalc,
    metaInfo: {
      title: messages.agroservices.seedingRate.title,
    },
    accessControlKey: 'routes.agroservicesSeedingRate',
  },
  {
    key: 'agroservices-fgis-zerno',
    path: EAgroservicesRoute.fgisZerno,
    exact: true,
    component: FgisZerno,
    metaInfo: {
      title: messages.agroservices.fgis.title,
    },
    accessControlKey: 'routes.agroservicesFgisZerno',
  },
  {
    key: 'agroservices-poleexpert',
    path: EAgroservicesRoute.poleExpert,
    exact: true,
    component: PoleExpert,
    asyncThunk: fetchAgroserviceProducts,
    metaInfo: {
      title: messages.agroservices.poleExpert.title,
      description: messages.agroservices.poleExpert.description,
    },
    accessControlKey: 'routes.agroservicesPoleexpert',
  },
  {
    key: 'agroservices-polescouting',
    path: EAgroservicesRoute.poleScouting,
    exact: true,
    component: PoleScouting,
    asyncThunk: fetchAgroserviceProducts,
    metaInfo: {
      title: messages.agroservices.poleScouting.title,
      description: messages.agroservices.poleScouting.description,
    },
    accessControlKey: 'routes.agroservicesPolescouting',
  },
  {
    key: 'agroservices-polediff',
    path: EAgroservicesRoute.poleDiff,
    exact: true,
    component: PoleDiff,
    asyncThunk: getAsyncThunks('agroserviceProducts'),
    metaInfo: {
      title: messages.agroservices.poleDiff.title,
      description: messages.agroservices.poleDiff.description,
    },
    accessControlKey: 'routes.agroservicesPolediff',
  },
  {
    key: 'agronout-farmland-crop-valuation',
    path: EAgroservicesRoute.farmlandCropValuation,
    exact: true,
    component: FarmlandCropValuation,
    asyncThunk: fetchAgroserviceProducts,
    metaInfo: {
      title: messages.agroservices.farmlandCropValuation.title,
    },
    accessControlKey: 'routes.agronoutFarmlandCropValuation',
  },
  {
    key: 'agroservices-poleplus',
    path: EAgroservicesRoute.polePlus,
    exact: true,
    component: PolePlus,
    asyncThunk: fetchAgroserviceProducts,
    metaInfo: {
      title: messages.agroservices.polePlus.title,
      description: messages.agroservices.polePlus.title,
    },
    accessControlKey: 'routes.agroservicesPolePlus',
  },
  {
    key: 'financingRedirect',
    path: [
      EFinancingOfferRoute.deprecatedMain,
      EFinancingOfferRoute.experimentalOldUrl,
      EFinancingOfferRoute.expressCreditAlfaBank,
      EFinancingOfferRoute.revolvingTinkoff,
      EFinancingOfferRoute.baltiyskiyLeasing,
      EFinancingOfferRoute.expressCredit,
    ],
    exact: true,
    component: FinancingRedirect,
    metaInfo: {
      robots: 'noindex',
    },
    accessControlKey: 'routes.financingRedirect',
  },
  {
    key: 'sberRassrochka',
    path: EFinancingOfferRoute.sberRassrochka,
    exact: true,
    component: SberRassrochka,
    accessControlKey: 'routes.sberRassrochka',
  },
  {
    key: 'investment',
    path: EFinancingOfferRoute.investmentCredit,
    exact: true,
    component: InvestmentCredit,
    accessControlKey: 'routes.investment',
  },
  {
    key: 'investSber',
    path: EFinancingOfferRoute.investSber,
    exact: true,
    component: InvestSber,
    accessControlKey: 'routes.investSber',
  },
  {
    key: 'advancePaymentOnFutureHarvest',
    path: EFinancingOfferRoute.advancePaymentOnFutureHarvest,
    exact: true,
    component: AdvancePaymentOnFutureHarvest,
    accessControlKey: 'routes.advancePaymentOnFutureHarvest',
  },
  {
    key: 'deprecatedAdvancePaymentOnFutureHarvest',
    path: EFinancingOfferRoute.deprecatedAdvancePaymentOnFutureHarvest,
    exact: true,
    component: AdvancePaymentOnFutureHarvestRedirect,
    redirect: {
      url: EFinancingOfferRoute.advancePaymentOnFutureHarvest,
      httpCode: 301,
    },
    accessControlKey: 'routes.advancePaymentOnFutureHarvestWithCalculator',
  },
  {
    key: 'agriculturalMachinesAndEquipmentLeasing',
    path: EFinancingOfferRoute.agriculturalMachinesAndEquipmentLeasing,
    exact: true,
    component: AgriculturalMachinesAndEquipmentLeasing,
    accessControlKey: 'routes.agriculturalMachinesAndEquipmentLeasing',
  },
  {
    key: 'alfabankLeasing',
    path: EFinancingOfferRoute.alfaLeasing,
    exact: true,
    component: AlfaLeasing,
    accessControlKey: 'routes.alfabankLeasing',
  },
  {
    key: 'lombardCreditAlfaBank',
    path: EFinancingOfferRoute.lombardCreditAlfaBank,
    exact: true,
    component: LombardCreditAlfaBank,
    accessControlKey: 'routes.lombardCreditAlfaBank',
  },
  {
    key: 'interleasing',
    path: EFinancingOfferRoute.interleasing,
    exact: true,
    component: Interleasing,
    accessControlKey: 'routes.interleasing',
  },
  {
    key: 'sberLeasing',
    path: EFinancingOfferRoute.sberLeasing,
    exact: true,
    component: SberLeasing,
    accessControlKey: 'routes.sberLeasing',
  },
  {
    key: 'stoneXxiLeasing',
    path: EFinancingOfferRoute.stoneXxiLeasing,
    exact: true,
    component: StoneXxiLeasing,
    accessControlKey: 'routes.stoneXxiLeasing',
  },
  {
    key: 'agroconsulting',
    path: EAgroservicesRoute.agroconsulting,
    exact: true,
    component: Agroconsulting,
    accessControlKey: 'routes.agroconsulting',
  },
  {
    key: 'revolving',
    path: EFinancingOfferRoute.revolving,
    exact: true,
    component: Revolving,
    accessControlKey: 'routes.revolving',
  },
  {
    key: 'oborotSber',
    path: EFinancingOfferRoute.oborotSber,
    exact: true,
    component: OborotSber,
    accessControlKey: 'routes.oborotSber',
  },
  {
    key: 'MFOPage',
    path: EFinancingOfferRoute.MFOPage,
    exact: true,
    component: MFOPage,
    accessControlKey: 'routes.mfo',
  },
  {
    key: 'FinancingAgentPage',
    path: EFinancingOfferRoute.FinancingAgentPage,
    exact: true,
    component: FinancingAgentPage,
    accessControlKey: 'routes.agent',
  },

  {
    key: 'transportAndSpecialEquipmentLeasing',
    path: EFinancingOfferRoute.transportAndSpecialEquipmentLeasing,
    exact: true,
    component: TransportAndSpecialEquipmentLeasing,
    accessControlKey: 'routes.transportAndSpecialEquipmentLeasing',
  },
  {
    key: 'CenterInvestLoan',
    path: EFinancingOfferRoute.centerInvestLoan,
    exact: true,
    component: CenterInvestLoan,
    accessControlKey: 'routes.centerInvestLoan',
  },
  {
    key: 'fleetFinanceLeasing',
    path: EFinancingOfferRoute.fleetFinanceLeasing,
    exact: true,
    component: FleetFinanceLeasing,
    accessControlKey: 'routes.fleetFinanceLeasing',
  },
  {
    key: 'agroservices',
    path: EAgroservicesRoute.main,
    exact: true,
    component: Agroservices,
    asyncThunk: fetchAgroserviceProducts,
    metaInfo: {
      title: messages.agroservices.title,
    },
    accessControlKey: 'routes.agroservices',
  },
  {
    key: 'feedback',
    path: ECommonRoute.feedback,
    component: Feedback,
    metaInfo: {
      title: messages.feedback.title,
    },
    accessControlKey: 'routes.feedback',
  },
  {
    key: 'faqList',
    path: [EFaqRouteUrl.QUESTION, EFaqRouteUrl.SPECIALIZATION, EFaqRouteUrl.CATEGORY, EFaqRouteUrl.AUTHOR],
    component: QuestionsAndAnswers,
    asyncThunk: getAsyncThunks(
      'faqCategories',
      { name: 'faqCategoryQuestions', shouldFetch: hasParamInUrl('specializationId') },
      {
        name: 'faqCategory',
        shouldFetch: (params, state) => {
          const hasCategoryIdInUrl = hasParamInUrl('categoryId')(params)

          return (hasCategoryIdInUrl && isDesktop()(state)) || (hasCategoryIdInUrl && !hasParamInUrl('specializationId')(params))
        },
      },
      { name: 'faqSpecialization', shouldFetch: (params, state) => hasParamInUrl('specializationId')(params) && !isDesktop()(state) },
      { name: 'faqArticle', shouldFetch: hasParamInUrl('questionId') },
      { name: 'faqAuthor', shouldFetch: hasParamInUrl('authorId') },
    ),
    getBreadcrumbs: getFaqBreadcrumbs,
    metaInfo: {
      title: messages.faq.title,
    },
    accessControlKey: 'routes.faqList',
  },
  {
    key: 'faqSearch',
    component: FaqSearch,
    path: EFaqRouteUrl.SEARCH,
    asyncThunk: getAsyncThunks('faqQuestions'),
    accessControlKey: 'routes.faqSearch',
  },
  {
    key: 'faq',
    path: '/faq',
    exact: true,
    asyncThunk: getAsyncThunks('faqCategories'),
    component: Faq,
    metaInfo: {
      title: messages.faq.title,
    },
    accessControlKey: 'routes.faq',
  },
  {
    key: 'loyalty',
    path: EUserProfileRoute.loyalty,
    exact: true,
    shouldBeAuthenticated: true,
    component: Loyalty,
    accessControlKey: 'routes.loyalty',
  },
  {
    key: 'infoportal',
    path: EInfoportalAdministrationRoute.MAIN,
    component: InfoportalAdministrationPage,
    shouldBeAuthenticated: true,
    metaInfo: {
      title: messages.infoportal.administration.title,
      robots: 'noindex',
    },
    accessControlKey: 'routes.infoportal',
  },
  {
    key: 'requisites',
    path: '/requisites',
    component: Requisites,
    metaInfo: {
      title: messages.requisites.title,
    },
    accessControlKey: 'routes.requisites',
  },
  {
    key: 'about-us',
    path: ECommonRoute.aboutUs,
    exact: true,
    component: AboutUs,
    metaInfo: {
      title: messages.aboutUs.title,
      description: messages.aboutUs.description,
    },
    accessControlKey: 'routes.aboutUs',
  },
  {
    key: 'journal-publication',
    path: `${EJournalRoute.PUBLICATION}/:publicationSlug`,
    exact: true,
    component: JournalPublication,
    ssrPrefetchQueries: journalPublicationSsrQueries,
    accessControlKey: 'routes.journalPublication',
  },
  {
    key: 'journal-news',
    path: [EJournalRoute.NEWS, `${EJournalRoute.NEWS}/:rubricId`],
    exact: true,
    component: JournalNews,
    ssrPrefetchQueries: journalNewsSsrQueries,
    metaInfo: {
      title: messages.journal.news.title,
    },
    accessControlKey: 'routes.journalNews',
  },
  {
    key: 'journal-news-latest',
    path: [`${EJournalRoute.NEWS}/:rubricId/latest`],
    exact: true,
    component: JournalRedirectToLatestNews,
    accessControlKey: 'routes.journalNewsLatest',
  },
  {
    key: 'journal',
    path: [EJournalRoute.MAIN],
    exact: true,
    component: Journal,
    ssrPrefetchQueries: journalIndexSsrQueries,
    metaInfo: {
      title: messages.journal.title,
    },
    accessControlKey: 'routes.journal',
  },
  {
    key: 'journal-by-rubric',
    path: EJournalRoute.RUBRIC,
    exact: true,
    component: Journal,
    ssrPrefetchQueries: journalByRubricSsrQueries,
    accessControlKey: 'routes.journalByRubric',
  },
  {
    key: 'journal-by-tag',
    path: EJournalRoute.TAG,
    exact: true,
    component: Journal,
    ssrPrefetchQueries: journalByTagSsrQueries,
    metaInfo: {
      title: messages.journal.title,
    },
    accessControlKey: 'routes.journalByTag',
  },
  {
    key: 'journal-search',
    path: EJournalRoute.SEARCH,
    exact: true,
    component: Journal,
    accessControlKey: 'routes.journalSearch',
  },
  {
    key: 'workingConditions',
    path: ELegalDocumentsRoute.WORKING_CONDDITIONS,
    exact: true,
    component: WorkingConditions,
    metaInfo: {
      title: messages.workingConditions.title,
    },
    accessControlKey: 'routes.workingConditions',
  },
  {
    key: 'legalDocuments',
    path: ELegalDocumentsRoute.DOCUMENT_VERSION,
    exact: true,
    component: LegalDocumentsPage,
    metaInfo: {
      title: messages.legalDocuments.title,
    },
    accessControlKey: 'routes.legalDocuments',
  },
  {
    key: 'partnerLanding',
    path: EPartnerLandingRoute.registrationPartners,
    exact: true,
    component: PartnerLanding,
    metaInfo: {
      title: messages.partnerLanding.title,
    },
    accessControlKey: 'routes.partnerLanding',
  },
  {
    key: 'partnerLandingRedirect',
    path: EPartnerLandingRoute.buyer,
    exact: true,
    component: PartnerLandingRedirect,
    metaInfo: {
      robots: 'noindex',
    },
    accessControlKey: 'routes.partnerLandingRedirect',
  },
  {
    key: 'vezdehodPromoLanding',
    path: EInputsPromoLandingRoute.vezdehod,
    exact: true,
    component: vezdehodPromoLanding,
    metaInfo: {
      title: messages.vezdehodPromoLanding.title,
    },
    accessControlKey: 'routes.vezdehodPromoLanding',
  },
  {
    key: 'businessProcess',
    path: ECommonRoute.businessProcess,
    shouldBeAuthenticated: true,
    exact: true,
    component: BusinessProcess,
    asyncThunk: getAsyncThunks('statusModel'),
    metaInfo: {
      title: messages.businessProcess.title,
    },
    accessControlKey: 'routes.businessProcess',
  },
  {
    key: 'SberAuthRedirect',
    path: ECommonRoute.sberAuthRedirect,
    exact: true,
    component: SberAuthRedirect,
    accessControlKey: 'routes.sberAuthRedirect',
  },
  {
    key: 'you-do',
    path: EAgroservicesRoute.youDo,
    exact: true,
    component: YouDo,
    metaInfo: {
      title: messages.agroservices.title,
    },
    accessControlKey: 'routes.youDo',
  },
  {
    key: 'efis',
    path: EAgroservicesRoute.efisZsn,
    exact: true,
    component: EfisZsn,
    metaInfo: {
      title: messages.agroservices.title,
    },
    accessControlKey: 'routes.efis',
  },
  {
    key: 'fieldEvaluationReport',
    path: EAgroservicesRoute.fieldEvaluationReport,
    exact: true,
    component: FieldEvaluationReport,
    metaInfo: {
      title: messages.fieldEvaluationReport.title,
    },
    accessControlKey: 'routes.fieldEvaluationReport',
  },
  {
    key: 'mobileRedirect',
    path: ECommonRoute.mobileRedirect,
    exact: true,
    component: MobileRedirect,
    accessControlKey: 'routes.mobileRedirect',
  },
  {
    key: 'logout',
    path: ECommonRoute.logout,
    exact: true,
    component: Logout,
    accessControlKey: 'routes.logout',
  },
]
